import React, { useEffect, useRef, useState } from 'react';
import ResponsiveImage from '@components/image/ResponsiveImage';
import { IMAGE_SIZES } from '@components/image/ResponsiveImage.sizes';
import { DynamicVideoPlayer } from '@components/video-player/DynamicVideoPlayer';
import { CloudinaryImageType, CloudinaryVideoType } from '@appTypes/CloudinaryImageType';
import { BenefitImageLoader } from '@components/skeleton-loaders/BenefitImageLoader';
import * as S from './BenefitImage.styles';

export interface BenefitImageProps {
  image?: CloudinaryImageType;
  video?: CloudinaryVideoType;
  partner: {
    partnerName: string;
    logo: CloudinaryImageType;
  };
}

export const BenefitImage = ({ image, video, partner }: BenefitImageProps) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!image || video) {
      setIsImageLoaded(false);
      return undefined;
    }

    setIsImageLoaded(false);

    const imgElement = imageRef.current?.querySelector('img');
    if (!imgElement) return undefined;

    if (imgElement.complete && imgElement.naturalHeight > 0) {
      setIsImageLoaded(true);
      return undefined;
    }

    const handleLoad = () => setIsImageLoaded(true);

    imgElement.addEventListener('load', handleLoad);

    return () => {
      imgElement.removeEventListener('load', handleLoad);
    };
  }, [image, video]);

  return (
    <S.BenefitHeaderContainer>
      {image && !video && (
        <S.ImageWrapper>
          <S.ImageFigure>
            {!isImageLoaded && (
              <S.ImageLoadingOverlay>
                <BenefitImageLoader />
              </S.ImageLoadingOverlay>
            )}
            <S.ImageInner ref={imageRef}>
              <ResponsiveImage
                imageId={image?.publicId}
                altText={image?.altText || image?.caption}
                sizes={IMAGE_SIZES.benefitHero}
                priority
              />
            </S.ImageInner>
          </S.ImageFigure>
          {partner?.logo && (
            <S.PartnerlogoWrapper>
              <img
                src={partner.logo.srcSet?.jpgLarge}
                alt={partner.partnerName ? `${partner.partnerName} logo` : 'Partnerlogo'}
              />
            </S.PartnerlogoWrapper>
          )}
        </S.ImageWrapper>
      )}
      {video && (
        <S.ImageWrapper>
          <DynamicVideoPlayer video={video} />
        </S.ImageWrapper>
      )}
    </S.BenefitHeaderContainer>
  );
};
